/**
 * Lender Library (Phase 1)
 * Route: /lenders
 *
 * Account-level library of the banks/lenders a developer works with. Each lender
 * stores contacts + the lender's own bid sheet and draw request form files. These
 * are reused across projects (assigned via a project's lender field) and will
 * power the pixel-exact form fill in Phase 2.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';
import {
  Landmark, Plus, Upload, Download, Trash2, FileSpreadsheet, FileText,
  Loader2, Building2, CheckCircle2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

const FORM_META = {
  bid_sheet: { label: 'Bid Sheet', icon: FileSpreadsheet, hint: 'The lender\u2019s bid sheet format contractors bid against.' },
  draw_form: { label: 'Draw Request Form', icon: FileText, hint: 'The lender\u2019s draw request form you submit for funding.' },
};

export default function Lenders({ user }) {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // `${lenderId}:${formType}` while uploading

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const res = await apiService.getLenders(user.id);
      setLenders(res.data || []);
    } catch (err) {
      toast.error('Failed to load lenders');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useResumeRefresh(load);

  const handleAddLender = async () => {
    if (!newName.trim()) { toast.error('Enter a lender name'); return; }
    setSaving(true);
    try {
      const contacts = newContact.name || newContact.email || newContact.phone ? [newContact] : [];
      await apiService.createLender({ user_id: user.id, name: newName.trim(), contacts });
      toast.success(`${newName.trim()} added`);
      setNewName('');
      setNewContact({ name: '', email: '', phone: '' });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lender');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLender = async (lender) => {
    if (!window.confirm(`Remove ${lender.name} from your library? Uploaded forms will be deleted.`)) return;
    try {
      await apiService.deleteLender(lender.id);
      toast.success('Lender removed');
      load();
    } catch (err) {
      toast.error('Failed to remove lender');
    }
  };

  const handleUpload = async (lenderId, formType, file) => {
    if (!file) return;
    const key = `${lenderId}:${formType}`;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiService.uploadLenderForm(lenderId, formType, fd);
      toast.success(`${FORM_META[formType].label} uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusyKey(null);
    }
  };

  const handleDownload = async (lender, formType) => {
    try {
      const res = await apiService.downloadLenderForm(lender.id, formType);
      const meta = lender[`${formType === 'bid_sheet' ? 'bid_sheet' : 'draw_form'}_file`];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', meta?.filename || `${formType}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleDeleteForm = async (lenderId, formType) => {
    try {
      await apiService.deleteLenderForm(lenderId, formType);
      toast.success('File removed');
      load();
    } catch (err) {
      toast.error('Failed to remove file');
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="lenders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#eff6ff' }}>
              <Landmark className="w-5 h-5" style={{ color: NAVY }} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">Lenders</h1>
              <p className="text-sm text-slate-500">Your banks &amp; their bid sheet and draw request forms</p>
            </div>
          </div>
        </div>
        <Button onClick={() => setAddOpen(true)} className="text-white" style={{ background: NAVY }} data-testid="add-lender-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add lender
        </Button>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : lenders.length === 0 ? (
        <Card className="border-dashed" data-testid="lenders-empty">
          <CardContent className="py-14 text-center">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-3">
              <Building2 className="w-6 h-6" style={{ color: NAVY }} />
            </div>
            <p className="font-bold text-slate-900">No lenders yet</p>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              Add each bank you work with and upload their bid sheet &amp; draw request form so your
              projects stay compliant with the exact forms they require.
            </p>
            <Button onClick={() => setAddOpen(true)} className="mt-5 text-white" style={{ background: NAVY }} data-testid="add-first-lender-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add your first lender
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {lenders.map((lender) => (
            <Card key={lender.id} data-testid={`lender-card-${lender.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    {lender.logo_url ? (
                      <img src={lender.logo_url} alt={lender.name} className="w-10 h-10 rounded-lg object-contain bg-slate-50" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f0fdf4' }}>
                        <Landmark className="w-5 h-5" style={{ color: GREEN }} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate">{lender.name}</p>
                      {lender.contacts?.[0]?.name && (
                        <p className="text-xs text-slate-400 truncate">
                          {lender.contacts[0].name}{lender.contacts[0].phone ? ` \u00b7 ${lender.contacts[0].phone}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteLender(lender)}
                    className="text-slate-300 hover:text-red-500 flex-shrink-0"
                    data-testid={`delete-lender-${lender.id}`}
                    aria-label="Remove lender"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  {Object.keys(FORM_META).map((formType) => {
                    const meta = lender[`${formType === 'bid_sheet' ? 'bid_sheet' : 'draw_form'}_file`];
                    const Icon = FORM_META[formType].icon;
                    const key = `${lender.id}:${formType}`;
                    const busy = busyKey === key;
                    return (
                      <div key={formType} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="w-4 h-4 flex-shrink-0" style={{ color: meta ? GREEN : '#94a3b8' }} />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800">{FORM_META[formType].label}</p>
                              {meta ? (
                                <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" style={{ color: GREEN }} /> {meta.filename}
                                </p>
                              ) : (
                                <p className="text-[11px] text-slate-400 truncate">{FORM_META[formType].hint}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {meta && (
                              <>
                                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleDownload(lender, formType)} data-testid={`download-${formType}-${lender.id}`}>
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-red-500 hover:text-red-600" onClick={() => handleDeleteForm(lender.id, formType)} data-testid={`remove-${formType}-${lender.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.xlsx,.xlsm,.xls,.doc,.docx"
                                disabled={busy}
                                onChange={(e) => { handleUpload(lender.id, formType, e.target.files?.[0]); e.target.value = ''; }}
                                data-testid={`upload-${formType}-${lender.id}`}
                              />
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 ${busy ? 'text-slate-400' : 'text-blue-700 hover:bg-blue-50'}`}>
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                {meta ? 'Replace' : 'Upload'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {lender.bid_sheet_file && lender.draw_form_file && (
                  <Badge className="mt-3" style={{ background: '#f0fdf4', color: GREEN }} data-testid={`lender-ready-${lender.id}`}>
                    Forms ready
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add lender dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-lender-dialog">
          <DialogHeader>
            <DialogTitle>Add a lender</DialogTitle>
            <DialogDescription>Add a bank you work with. You can upload their forms right after.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="lender-name">Lender name *</Label>
              <Input id="lender-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Hills Bank" className="mt-1.5" data-testid="lender-name-input" />
            </div>
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <p className="text-xs font-semibold text-slate-500">Primary contact (optional)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Name" data-testid="lender-contact-name" />
                <Input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" data-testid="lender-contact-email" />
                <Input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone" data-testid="lender-contact-phone" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={handleAddLender} disabled={saving} className="text-white" style={{ background: NAVY }} data-testid="save-lender-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Add lender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
